import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { ImportDialog } from '../../components/ui/ImportDialog'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  /** Clasificarea NOASTRĂ. Poate diferi de grupa din EA — vezi `grupaEa`. */
  category: string
  /** Grupa așa cum o știe ExpertAccounts. `null` la produsele care n-au venit din EA. */
  grupaEa: string | null
  /** „Tip articol" din EA. Filtrul de la Comenzi se uita pe el — un camp gol scoate articolul. */
  itemType: string | null
  ean: string | null
  costProduction: string | null
  vatRate: number
  active: boolean
  isService: boolean
  /** Greutatea unei bucăți, în grame. Din ea se calculează coletul la expedierile cu AWB. */
  greutateGrame: number | null
}

const ProductSchema = z.object({
  sku: z
    .string()
    .regex(/^[A-Za-z0-9_+\-. ()]{1,60}$/, 'SKU: litere, cifre, _ + - . spațiu ( )')
    .max(60),
  name: z.string().min(1, 'Denumire obligatorie'),
  brand: z.string().min(1, 'Brand obligatoriu'),
  category: z.string().min(1, 'Categorie obligatorie'),
  ean: z.string().optional(),
  costProduction: z.union([z.coerce.number().nonnegative(), z.literal('')]).optional(),
  vatRate: z.coerce.number().int().min(0).max(99),
  active: z.boolean().default(true),
  isService: z.boolean().default(false),
  greutateGrame: z.union([z.coerce.number().int().positive().max(50000), z.literal('')]).optional(),
})

type ProductInput = z.infer<typeof ProductSchema>

interface GrupaEa {
  grupa: string
  total: number
  lipsa: number
  exemple: string[]
}

/** Mesajul de la API, cand exista; altfel textul erorii. */
function mesajEroare(e: unknown): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response
  return r?.data?.error?.message ?? (e as Error)?.message ?? 'Eroare necunoscută'
}

export function SetariProduseUserPage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('settings.products:write')
  const [editTarget, setEditTarget] = useState<Product | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [eaOpen, setEaOpen] = useState(false)
  const [greutatiOpen, setGreutatiOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [brandFilter, setBrandFilter] = useState<string>('')
  const [categorieFilter, setCategorieFilter] = useState<string>('')
  const [grupaFilter, setGrupaFilter] = useState<string>('')

  const query = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })

  const createMutation = useMutation({
    mutationFn: async (data: ProductInput) => (await api.post('/products', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Produs adăugat')
      setCreateOpen(false)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la adăugare'
      toast.error(message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProductInput }) =>
      (await api.patch(`/products/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Produs actualizat')
      setEditTarget(null)
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Eroare la actualizare'
      toast.error(message)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/products/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      toast.success('Produs șters')
    },
  })

  const products = useMemo(() => query.data?.products ?? [], [query.data])
  const brands = useMemo(() => Array.from(new Set(products.map((p) => p.brand))).sort(), [products])
  /*
    Doua clasificari, si nu sunt acelasi lucru.

    Alexandru: „in expert avem categorie si grupa, putem avea si in CRM?". `category` e a
    NOASTRA — suplimentele stau in EA la „Suplimente alimentare", dar aici au „Vitamine si
    minerale", fiindca asa le-a clasificat marketingul. `grupaEa` spune ce zice ERP-ul.

    La materialele promotionale se nimeresc identice, de-aia parea ca e un singur lucru.
  */
  const categorii = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort(),
    [products],
  )
  const grupe = useMemo(
    () =>
      Array.from(new Set(products.map((p) => p.grupaEa).filter((g): g is string => !!g))).sort(),
    [products],
  )
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return products.filter((p) => {
      if (brandFilter && p.brand !== brandFilter) return false
      if (categorieFilter && p.category !== categorieFilter) return false
      if (grupaFilter && p.grupaEa !== grupaFilter) return false
      if (!term) return true
      return (
        p.sku.toLowerCase().includes(term) ||
        p.name.toLowerCase().includes(term) ||
        (p.ean ?? '').includes(term)
      )
    })
  }, [products, search, brandFilter, categorieFilter, grupaFilter])

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Catalog produse</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setEaOpen(true)}>
              Adaugă din ExpertAccounts
            </Button>
            <Button variant="secondary" onClick={() => setImportOpen(true)}>
              Import CSV / XLSX
            </Button>
            <Button variant="secondary" onClick={() => setGreutatiOpen(true)}>
              Greutăți produse
            </Button>
            <Button onClick={() => setCreateOpen(true)}>+ Adaugă produs</Button>
          </div>
        )}
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după SKU, denumire sau EAN…"
            className="rounded border border-b4l-border px-3 py-1.5 text-sm w-64"
          />
          <select
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
          >
            <option value="">Toate brand-urile</option>
            {brands.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            value={categorieFilter}
            onChange={(e) => setCategorieFilter(e.target.value)}
            className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
          >
            <option value="">Toate categoriile</option>
            {categorii.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          {grupe.length > 0 && (
            <select
              value={grupaFilter}
              onChange={(e) => setGrupaFilter(e.target.value)}
              className="rounded border border-b4l-border bg-white px-3 py-1.5 text-sm"
            >
              <option value="">Toate grupele din EA</option>
              {grupe.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          )}
          <span className="ml-auto text-xs text-b4l-text-muted">
            {filtered.length} / {products.length} produse
          </span>
        </div>

        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă…</p>
        ) : products.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun produs. {canWrite && 'Importă din CSV / XLSX sau adaugă manual.'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">SKU</th>
                <th className="py-2 pr-3 font-medium">Denumire</th>
                <th className="py-2 pr-3 font-medium">Brand</th>
                <th className="py-2 pr-3 font-medium">Categorie</th>
                <th className="py-2 pr-3 font-medium">Grupa</th>
                <th className="py-2 pr-3 font-medium">Tip articol</th>
                <th className="py-2 pr-3 font-medium">EAN</th>
                <th className="py-2 pr-3 font-medium text-right">Cost</th>
                <th className="py-2 pr-3 font-medium">TVA</th>
                <th className="py-2 pr-3 font-medium text-right">Greutate</th>
                {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{p.sku}</td>
                  <td className="py-2 pr-3">{p.name}</td>
                  <td className="py-2 pr-3">
                    <span className="rounded bg-b4l-bg px-2 py-0.5 text-[10px]">{p.brand}</span>
                  </td>
                  <td className="py-2 pr-3 text-xs text-b4l-text-muted">{p.category ?? '—'}</td>
                  <td className="py-2 pr-3 text-xs text-b4l-text-muted">{p.grupaEa ?? '—'}</td>
                  {/*
                    Tipul de articol, pe rosu cand lipseste.

                    Nu e decor: filtrul de la Comenzi se uita exact pe campul asta, iar un articol
                    fara el nu se poate comanda oricat de corecte ar fi regulile de pret. Pe 27.08
                    creatina a stat asa, si nimic din ecran nu spunea de ce. Acum se vede.
                  */}
                  <td className="py-2 pr-3 text-xs">
                    {p.itemType ? (
                      <span className="text-b4l-text-muted">{p.itemType}</span>
                    ) : (
                      <span
                        className="text-b4l-red"
                        title="Fără tip de articol nu se poate comanda. Rulează „Sincronizează catalog” din Setări → Comenzi (prag + prețuri)."
                      >
                        lipsește
                      </span>
                    )}
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-b4l-text-muted">
                    {p.ean ?? '—'}
                  </td>
                  <td className="py-2 pr-3 text-right font-mono">
                    {p.costProduction ? Number(p.costProduction).toFixed(2) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-xs">{p.vatRate}%</td>
                  {/*
                    Greutatea, pe roșu când lipsește — același tipar ca la tipul de articol, și
                    din același motiv: fără ea nu se poate emite niciun AWB, iar lipsa nu se vede
                    decât în clipa în care cineva încearcă să trimită un colet.
                  */}
                  <td className="py-2 pr-3 text-right font-mono text-xs">
                    {p.greutateGrame ? (
                      `${p.greutateGrame} g`
                    ) : p.isService ? (
                      <span className="text-b4l-text-dim">—</span>
                    ) : (
                      <span
                        className="text-b4l-red"
                        title="Fără greutate nu se poate emite AWB. Completeaz-o aici sau lipește-le în bloc, cu „Greutăți din Excel”."
                      >
                        lipsește
                      </span>
                    )}
                  </td>
                  {canWrite && (
                    <td className="py-2 pr-3">
                      <div className="flex gap-1">
                        <Button
                          variant="secondary"
                          onClick={() => setEditTarget(p)}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Editează
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            if (confirm(`Ștergi produsul ${p.sku}?`)) deleteMutation.mutate(p.id)
                          }}
                          className="!px-2 !py-1 !text-xs"
                        >
                          Șterge
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Create modal */}
      {createOpen && (
        <ProductFormModal
          title="Adaugă produs"
          submitLabel="Creează"
          onClose={() => setCreateOpen(false)}
          onSubmit={(data) => createMutation.mutate(data)}
          submitting={createMutation.isPending}
        />
      )}

      {/* Edit modal */}
      {editTarget && (
        <ProductFormModal
          title={`Editează ${editTarget.sku}`}
          submitLabel="Salvează"
          initial={{
            sku: editTarget.sku,
            name: editTarget.name,
            brand: editTarget.brand,
            category: editTarget.category,
            ean: editTarget.ean ?? '',
            costProduction: editTarget.costProduction ? Number(editTarget.costProduction) : '',
            vatRate: editTarget.vatRate,
            greutateGrame: editTarget.greutateGrame ?? '',
            isService: editTarget.isService,
            active: editTarget.active,
          }}
          onClose={() => setEditTarget(null)}
          onSubmit={(data) => updateMutation.mutate({ id: editTarget.id, data })}
          submitting={updateMutation.isPending}
        />
      )}

      {eaOpen && <ModalDinEa onClose={() => setEaOpen(false)} />}

      {greutatiOpen && <ModalGreutati onClose={() => setGreutatiOpen(false)} />}
      <ImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/products/import"
        title="Import catalog produse"
        hint="Coloane acceptate (RO sau EN): SKU/cod, denumire/name, brand/marca, categorie/category, EAN, cost, TVA. SKU-urile existente sunt actualizate, restul adăugate."
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['products'] })}
      />
    </div>
  )
}

interface ProductFormModalProps {
  title: string
  submitLabel: string
  initial?: ProductInput
  onClose: () => void
  onSubmit: (data: ProductInput) => void
  submitting: boolean
}

function ProductFormModal({
  title,
  submitLabel,
  initial,
  onClose,
  onSubmit,
  submitting,
}: ProductFormModalProps) {
  const form = useForm<ProductInput>({
    resolver: zodResolver(ProductSchema),
    defaultValues: initial ?? {
      sku: '',
      name: '',
      brand: 'Boost4Life',
      category: 'Vitamine si minerale',
      ean: '',
      costProduction: '',
      vatRate: 19,
      greutateGrame: '',
      active: true,
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={() => form.handleSubmit(onSubmit)()} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <form className="space-y-3" noValidate onSubmit={(e) => e.preventDefault()}>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="SKU"
            {...form.register('sku')}
            error={form.formState.errors.sku?.message}
            disabled={!!initial}
          />
          <Field
            label="EAN (8/12/13/14 cifre)"
            {...form.register('ean')}
            error={form.formState.errors.ean?.message}
          />
        </div>
        <Field
          label="Denumire"
          {...form.register('name')}
          error={form.formState.errors.name?.message}
        />
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Brand"
            {...form.register('brand')}
            error={form.formState.errors.brand?.message}
          />
          <Field
            label="Categorie"
            {...form.register('category')}
            error={form.formState.errors.category?.message}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Cost productie (RON)"
            type="number"
            step="0.01"
            {...form.register('costProduction')}
            error={form.formState.errors.costProduction?.message}
          />
          <Field
            label="Greutate (grame)"
            type="number"
            {...form.register('greutateGrame')}
            error={form.formState.errors.greutateGrame?.message}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="TVA (%)"
            type="number"
            {...form.register('vatRate')}
            error={form.formState.errors.vatRate?.message}
          />
          {/* Catalogul EA tine serviciile la un loc cu marfa; asta le scoate din rapoarte. */}
          <label className="col-span-2 flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" {...form.register('isService')} />
            <span>
              Nu e marfă (serviciu, discount, transport, ambalaj)
              <span className="block text-xs text-b4l-text-muted">
                Se exclude din rapoartele de sell-in / sell-out ale clienților.
              </span>
            </span>
          </label>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Aduce articole din ExpertAccounts, pe grupe.
 *
 * Alexandru, 21.08.2026: „e o problemă cu articolele, nu apar toate în CRM, putem adăuga toate
 * articolele cu categoria Materiale Promoționale?". În catalog era un singur pix, deși ERP-ul le
 * are pe toate — sincronizarea de prețuri actualizează doar ce există deja aici, nu creează.
 *
 * Se arată grupele din EA cu numărul de articole care lipsesc, ca alegerea să se facă văzând,
 * nu ghicind. Produsele existente nu se ating.
 */
function ModalDinEa({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient()
  const [grupa, setGrupa] = useState('')
  const [esteMostra, setEsteMostra] = useState(true)

  const grupe = useQuery<{ grupe: GrupaEa[] }>({
    queryKey: ['products', 'ea', 'grupe'],
    queryFn: async () => (await api.get<{ grupe: GrupaEa[] }>('/products/ea/grupe')).data,
  })

  const importa = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ create: number; sarite: number }>(
          '/products/ea/import',
          { grupa, esteMostra },
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: (r) => {
      toast.success(
        r.create > 0
          ? `${r.create} articole adăugate în catalog`
          : 'Nu era nimic de adăugat — toate există deja',
      )
      void queryClient.invalidateQueries({ queryKey: ['products'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(mesajEroare(e)),
  })

  const aleasa = grupe.data?.grupe.find((g) => g.grupa === grupa)

  return (
    <Modal open onClose={onClose} title="Adaugă articole din ExpertAccounts">
      <div className="space-y-3 text-sm">
        {grupe.isLoading && <p className="text-b4l-text-muted">Citesc catalogul din EA…</p>}
        {grupe.isError && (
          <p className="text-b4l-red">
            Nu am putut citi catalogul din EA. Verifică endpointul „items” din Setări → Comenzi.
          </p>
        )}
        {grupe.data && (
          <>
            <label className="block">
              <span className="mb-1 block text-xs text-b4l-text-muted">Grupa din EA</span>
              <select
                value={grupa}
                onChange={(e) => setGrupa(e.target.value)}
                className="w-full rounded border border-b4l-border px-3 py-1.5"
              >
                <option value="">Alege grupa…</option>
                {grupe.data.grupe.map((g) => (
                  <option key={g.grupa} value={g.grupa}>
                    {g.grupa} — {g.lipsa} lipsesc din {g.total}
                  </option>
                ))}
              </select>
            </label>

            {aleasa && (
              <div className="rounded-lg bg-b4l-bg p-2 text-xs">
                {aleasa.lipsa === 0 ? (
                  <p>Toate cele {aleasa.total} articole din grupă există deja în catalog.</p>
                ) : (
                  <>
                    <p className="mb-1">
                      Se adaugă <strong>{aleasa.lipsa}</strong> articole. Cele{' '}
                      {aleasa.total - aleasa.lipsa} care există deja rămân neatinse.
                    </p>
                    <ul className="list-disc pl-4 text-b4l-text-muted">
                      {aleasa.exemple.map((n) => (
                        <li key={n}>{n}</li>
                      ))}
                      {aleasa.lipsa > aleasa.exemple.length && (
                        <li>și încă {aleasa.lipsa - aleasa.exemple.length}…</li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            )}

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={esteMostra}
                onChange={(e) => setEsteMostra(e.target.checked)}
              />
              <span>Se pot da la sampling (apar în transferuri și pe bonul de consum)</span>
            </label>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="secondary" onClick={onClose}>
                Renunță
              </Button>
              <Button
                onClick={() => importa.mutate()}
                disabled={!grupa || !aleasa?.lipsa || importa.isPending}
              >
                {importa.isPending ? 'Adaug…' : 'Adaugă în catalog'}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  )
}

/**
 * Completarea greutăților: întâi din ExpertAccounts, prin API; lipirea din Excel e rezerva.
 *
 * Butonul s-a numit la început „Greutăți din Excel", iar Alexandru a întrebat, firesc, „dar nu
 * le putem aduce prin API din expert?" — deși exact asta făcea blocul de sus. Un nume care
 * ascunde calea principală sub rezerva ei e un nume greșit.
 *
 * Câmpul există din 30.09.2026, pentru AWB-urile FAN, dar n-avea nicăieri un loc unde să fie
 * completat: 205 produse active, toate fără greutate. Una câte una nu se completează — s-ar face
 * treizeci și s-ar opri.
 *
 * Două coloane, SKU și grame, în ordinea asta. SKU fiindcă ăla e în fișierele lui și ăla e cheia
 * din ExpertAccounts. Se acceptă și tab (din Excel), și punct-și-virgulă, și virgulă: nu merită
 * să refuzi o lipire pentru că despărțitorul n-a fost cel așteptat.
 *
 * Nu se creează produse noi. Un SKU necunoscut se întoarce, cu numele lui, ca să se vadă ce n-a
 * intrat — un import care tace despre ce-a sărit e mai rău decât unul care refuză.
 */
function ModalGreutati({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [text, setText] = useState('')

  const linii = useMemo(() => {
    const out: { sku: string; greutateGrame: number }[] = []
    const rele: string[] = []
    for (const rand of text.split('\n')) {
      const r = rand.trim()
      if (!r) continue
      const p = r.split(/[\t;,]/).map((x) => x.trim())
      if (p.length < 2) {
        rele.push(r)
        continue
      }
      const sku = p[0]
      const g = Number(p[1].replace(',', '.'))
      // Antetul lipit odată cu datele nu e o eroare, e un rând de sărit.
      if (!sku || !Number.isFinite(g) || g <= 0) {
        if (!/^(sku|cod|produs)$/i.test(sku)) rele.push(r)
        continue
      }
      out.push({ sku, greutateGrame: Math.round(g) })
    }
    return { bune: out, rele }
  }, [text])

  const trimite = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ actualizate: number; negasite: string[]; ramaseFaraGreutate: number }>(
          '/products/greutati',
          { linii: linii.bune },
        )
      ).data,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      if (r.negasite.length > 0) {
        toast.warning(
          `${r.actualizate} completate. ${r.negasite.length} SKU-uri nu există în catalog: ` +
            r.negasite.slice(0, 8).join(', ') +
            (r.negasite.length > 8 ? '…' : ''),
          { duration: 15_000 },
        )
      } else {
        toast.success(
          `${r.actualizate} greutăți completate.` +
            (r.ramaseFaraGreutate > 0
              ? ` Mai sunt ${r.ramaseFaraGreutate} produse fără greutate.`
              : ' Toate produsele au acum greutate.'),
        )
      }
      onClose()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err?.response?.data?.error?.message ?? 'Nu s-au putut salva greutățile.')
    },
  })

  return (
    <Modal
      open
      onClose={onClose}
      title="Greutățile produselor"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Renunță
          </Button>
          <Button
            disabled={linii.bune.length === 0}
            loading={trimite.isPending}
            onClick={() => trimite.mutate()}
          >
            Completează {linii.bune.length > 0 ? `${linii.bune.length} produse` : ''}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <BlocGreutatiEa onGata={onClose} />

        <div className="border-t border-b4l-border pt-3 text-xs font-medium text-b4l-text-muted">
          Sau lipește-le de mână
        </div>
        <p className="text-sm text-b4l-text-muted">
          Lipește două coloane din Excel: <strong>SKU</strong> și{' '}
          <strong>greutatea în grame</strong>, în ordinea asta. Antetul poate rămâne — se sare peste
          el.
        </p>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={12}
          placeholder={'VD3K210\t180\nMG60\t95\nCREA500\t560'}
          className="w-full rounded border border-b4l-border px-2 py-1.5 font-mono text-xs"
        />
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-b4l-green-dark">{linii.bune.length} rânduri bune</span>
          {linii.rele.length > 0 && (
            <span className="text-b4l-red" title={linii.rele.slice(0, 10).join(' | ')}>
              {linii.rele.length} rânduri pe care nu le înțeleg — treci cu mouse-ul să le vezi
            </span>
          )}
        </div>
        <p className="text-xs text-b4l-text-dim">
          Greutatea e a <strong>unei bucăți</strong>, fără ambalajul coletului. La expediere se
          adună pe cantități, iar cifra rămâne editabilă — plicul și folia adaugă.
        </p>
      </div>
    </Modal>
  )
}

interface PreviewEa {
  rezumat: {
    totalEa: number
    inCatalog: number
    cuWeight: number
    cuGweight: number
    weightEgalUnu: number
    gweightEgalUnu: number
    weightMin: number | null
    weightMax: number | null
    gweightMin: number | null
    gweightMax: number | null
  }
  propunere: { camp: 'weight' | 'gweight'; unitate: 'kg' | 'g'; motiv: string }
}

/**
 * Aducerea greutăților din ExpertAccounts, cu cifrele la vedere înainte de scriere.
 *
 * Alexandru, 01.10.2026: „greutățile există în expert în items în weight și gweight".
 *
 * Nu se scrie nimic până nu se vede ce e acolo. EA are două câmpuri, iar noi nu știm nici care
 * e completat, nici în ce unitate, nici câte articole au un `1` pus implicit la crearea fișei.
 * Scris orbește, un `1` devine 1000 de grame pe fiecare produs — iar atunci nimeni nu mai
 * verifică, fiindcă nu mai arată gol. Programul propune; omul alege.
 */
function BlocGreutatiEa({ onGata }: { onGata: () => void }) {
  const qc = useQueryClient()
  const [camp, setCamp] = useState<'weight' | 'gweight' | ''>('')
  const [unitate, setUnitate] = useState<'kg' | 'g' | ''>('')
  const [peste, setPeste] = useState(false)

  const preview = useQuery<PreviewEa>({
    queryKey: ['greutati-ea'],
    queryFn: async () => (await api.get<PreviewEa>('/products/greutati/ea')).data,
  })

  const p = preview.data
  const campAles = camp || p?.propunere.camp || 'gweight'
  const unitateAleasa = unitate || p?.propunere.unitate || 'g'

  const aplica = useMutation({
    mutationFn: async () =>
      (
        await api.post<{
          actualizate: number
          faraValoare: number
          inafaraCatalogului: number
          ramaseFaraGreutate: number
        }>('/products/greutati/ea', {
          camp: campAles,
          unitate: unitateAleasa,
          pesteCeleExistente: peste,
        })
      ).data,
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['products'] })
      toast.success(
        `${r.actualizate} greutăți aduse din ExpertAccounts.` +
          (r.ramaseFaraGreutate > 0
            ? ` Mai sunt ${r.ramaseFaraGreutate} produse fără greutate.`
            : ' Toate produsele au acum greutate.'),
        { duration: 10_000 },
      )
      onGata()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: { message?: string } } } }
      toast.error(err?.response?.data?.error?.message ?? 'Nu s-au putut aduce greutățile.')
    },
  })

  if (preview.isLoading) {
    return <p className="text-sm text-b4l-text-muted">Se citesc greutățile din ExpertAccounts…</p>
  }
  if (preview.isError || !p) {
    return (
      <p className="text-sm text-b4l-red">
        Nu s-a putut citi catalogul din ExpertAccounts. Lipește greutățile de mână, mai jos.
      </p>
    )
  }

  const interval = (min: number | null, max: number | null) =>
    min == null ? '—' : min === max ? String(min) : `${min} … ${max}`

  return (
    <div className="space-y-2 rounded border border-b4l-border bg-b4l-bg-soft p-3">
      <div className="text-sm font-medium">Adu-le prin API din ExpertAccounts</div>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-b4l-text-muted">
            <th className="font-medium">câmp EA</th>
            <th className="font-medium">completat la</th>
            <th className="font-medium">valori</th>
            <th
              className="font-medium"
              title="Un „1” peste tot e o valoare pusă implicit la crearea fișei, nu o cântărire."
            >
              exact 1
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="py-0.5">
              <code>gweight</code> (brut)
            </td>
            <td>
              {p.rezumat.cuGweight} / {p.rezumat.inCatalog}
            </td>
            <td>{interval(p.rezumat.gweightMin, p.rezumat.gweightMax)}</td>
            <td className={p.rezumat.gweightEgalUnu > 0 ? 'text-b4l-red' : ''}>
              {p.rezumat.gweightEgalUnu}
            </td>
          </tr>
          <tr>
            <td className="py-0.5">
              <code>weight</code> (net)
            </td>
            <td>
              {p.rezumat.cuWeight} / {p.rezumat.inCatalog}
            </td>
            <td>{interval(p.rezumat.weightMin, p.rezumat.weightMax)}</td>
            <td className={p.rezumat.weightEgalUnu > 0 ? 'text-b4l-red' : ''}>
              {p.rezumat.weightEgalUnu}
            </td>
          </tr>
        </tbody>
      </table>

      <p className="text-xs text-b4l-text-muted">{p.propunere.motiv}</p>

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select
          value={campAles}
          onChange={(e) => setCamp(e.target.value as 'weight' | 'gweight')}
          className="rounded border border-b4l-border px-2 py-1"
        >
          <option value="gweight">gweight — greutate brută</option>
          <option value="weight">weight — greutate netă</option>
        </select>
        <select
          value={unitateAleasa}
          onChange={(e) => setUnitate(e.target.value as 'kg' | 'g')}
          className="rounded border border-b4l-border px-2 py-1"
        >
          <option value="kg">sunt kilograme</option>
          <option value="g">sunt grame</option>
        </select>
        <label className="flex items-center gap-1">
          <input type="checkbox" checked={peste} onChange={(e) => setPeste(e.target.checked)} />
          peste cele completate deja
        </label>
        <Button
          className="!px-2 !py-1 !text-xs"
          loading={aplica.isPending}
          onClick={() => aplica.mutate()}
        >
          Adu greutățile
        </Button>
      </div>
    </div>
  )
}
